import type { IPlaybackScheduleRoot } from '@lucimark/shared/models/player-registration-payload'
import type { TFrame } from '@lucimark/shared/models/playlist'
import type { IQueueFrame } from '@/types'
import { countEmbeddedPlaylistRotatingAnchors } from '@lucimark/shared/helpers/selectEmbeddedFrames'

function embeddedFramesForUrn(
  embedded: Record<string, TFrame[]> | undefined,
  urn: string
): TFrame[] | null {
  if (embedded == null || urn === '') {
    return null
  }
  const direct = embedded[urn]
  if (direct != null) {
    return direct
  }
  const key = Object.keys(embedded).find(k => k.toLowerCase() === urn.toLowerCase())
  return key != null ? embedded[key]! : null
}

/** Durations (seconds) of each rotating anchor in an embedded house pool (bond tails excluded). */
export function houseEditorialAnchorDurationsSec(frames: readonly TFrame[]): number[] {
  const durations: number[] = []
  for (let i = 0; i < frames.length; i++) {
    if (i === 0 || frames[i - 1]?.playContinuousWithNext !== true) {
      const d = frames[i]?.duration
      if (typeof d === 'number' && d > 0) {
        durations.push(d)
      }
    }
  }
  return durations
}

/**
 * Full house editorial rotation cycle (ms) when the materialized queue is a single rotating pick.
 * Timer overlay should show 2s + 3s = 5s, not only the on-air frame duration.
 */
export function houseEditorialRotationCycleDurationMs(
  campaign: IPlaybackScheduleRoot | null,
  queue: readonly IQueueFrame[]
): number | null {
  if (campaign == null || queue.length !== 1) {
    return null
  }
  const embedUrn = queue[0]?.contentSourcePlaylistUrn?.trim() ?? ''
  if (embedUrn === '') {
    return null
  }
  const pool = embeddedFramesForUrn(campaign.embeddedPlaylistFrames, embedUrn)
  if (pool == null || countEmbeddedPlaylistRotatingAnchors(pool) <= 0) {
    return null
  }
  const totalMs = houseEditorialAnchorDurationsSec(pool).reduce((acc, sec) => acc + sec * 1000, 0)
  return totalMs > 0 ? totalMs : null
}

/** Elapsed ms within the full house rotation for the frame currently on screen. */
export function houseEditorialRotationElapsedMs(
  campaign: IPlaybackScheduleRoot | null,
  queue: readonly IQueueFrame[],
  onAirFrame: IQueueFrame,
  frameElapsedMs: number
): number | null {
  if (campaign == null || queue.length !== 1) {
    return null
  }
  const embedUrn = queue[0]?.contentSourcePlaylistUrn?.trim() ?? ''
  const pool = embeddedFramesForUrn(campaign.embeddedPlaylistFrames, embedUrn)
  if (pool == null) {
    return null
  }
  let beforeMs = 0
  for (let i = 0; i < pool.length; i++) {
    if (i === 0 || pool[i - 1]?.playContinuousWithNext !== true) {
      const id = pool[i]?.id
      const durMs = Math.max(0, (pool[i]?.duration ?? 0) * 1000)
      if (id === onAirFrame.id) {
        return beforeMs + Math.min(Math.max(0, frameElapsedMs), durMs)
      }
      beforeMs += durMs
    }
  }
  return null
}
